import React, { useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { createLoadingSelector, showModal, hideModal } from 'erisxkit/client';
import get from 'lodash/get';
import Page from '../common/Page';
import EMPTable8 from '../common/EMPTable8/EMPTable8';
import {
  deleteCgmPromiseCreator,
  fetchCgmTMEList,
  getCGMTMEList,
  updateCgmPromiseCreator,
} from '../reducers/CGMManagement/cgmManagementReducer';
import { DELETE_CGM } from '../reducers/CGMManagement/cgmManagementActions';
import { FETCH_CGM_LIST } from '../reducers/CGMManagement/cgmManagementActions';
import StyledButton from '../common/StyledButton';
import { sizes } from '../styles/styled';
import { ADD_NEW_CGM_TME, GENERIC_MODAL } from '../constants/modalTypes';
import { TableEditState } from '../common/EMPTable8/types';
import { CGM } from '../ts/models/CGM.model';
import {
  getActiveOrFirstAccountId,
  getActiveOrFirstAccountLabel,
} from '../reducers/activeAccountSelectors';
import { Row } from '@tanstack/react-table';
import { GenericModalProps } from '../common/components/GenericModal';
import colors from '../constants/colors';
import { getLoggedInUseCanWritePreTradeRisk } from '../reducers/userReducer';
import AccountSelectorFCM from './AccountSelector';
import cgmManagementTMEMetadata from '../metadata/cgmManagementTMEMetadata';
import { RootState } from '../ts/types/store';

const Header = styled.div`
  display: flex;
  justify-content: space-between;
`;

const AddCGM = styled(StyledButton)`
  width: 250px;
  height: 40px;
  margin: 15px 0px;
  @media (max-width: ${sizes.TINY}) {
    width: 100px;
  }
`;

const CgmManagement = () => {
  const firstRender = useRef(true);
  const dispatch = useDispatch();
  const accountId: string = useSelector(getActiveOrFirstAccountId);
  const accountLabel: string = useSelector(getActiveOrFirstAccountLabel);
  const canWrite = useSelector(getLoggedInUseCanWritePreTradeRisk);
  const data: CGM[] = useSelector(getCGMTMEList);
  const loading = useSelector((state) =>
    createLoadingSelector([FETCH_CGM_LIST])(state),
  );

  const deleteLoading = useSelector((state) =>
    createLoadingSelector([DELETE_CGM])(state),
  );

  useEffect(() => {
    if (!firstRender.current && accountLabel) {
      fetchCgms();
    } else {
      firstRender.current = false;
    }
  }, [accountLabel, dispatch]);

  const fetchCgms = useCallback(
    () =>
      dispatch(
        fetchCgmTMEList({
          urlParams: { accountLabel },
          queryParams: {
            isTme: true,
          },
        }),
      ),
    [accountLabel, dispatch],
  );

  const closeModal = useCallback(
    () => dispatch(hideModal(ADD_NEW_CGM_TME)),
    [dispatch],
  );

  const onAddNewCgm = () =>
    dispatch(
      showModal(ADD_NEW_CGM_TME, {
        size: 'large',
        onSuccess: fetchCgms,
        onGoBack: closeModal,
      }),
    );

  const onDeleteCgmSuccess = useCallback(() => {
    closeModal();
    fetchCgms();
  }, [closeModal, fetchCgms]);

  const onDeleteCgmFailed = useCallback(() => closeModal(), [closeModal]);
  const onDeleteCgm = useCallback(
    (cgmNumber) => {
      deleteCgmPromiseCreator(
        {
          urlParams: { accountLabel, cgmNumber },
          queryParams: {
            isTme: true,
          },
        },
        dispatch,
      ).then(onDeleteCgmSuccess, onDeleteCgmFailed);
    },
    [accountLabel, dispatch, onDeleteCgmFailed, onDeleteCgmSuccess],
  );

  const onDeleteClicked = useCallback(
    (row: Row<CGM>) => {
      const cgmNumber = get(row, 'original.cgmNumber', '-');
      dispatch(
        showModal(GENERIC_MODAL, {
          header: 'Confirm Removal',
          content: `Are you sure you want to remove CGM: ${cgmNumber} ?`,
          cancelProps: {
            text: 'Cancel',
            onClick: () => dispatch(hideModal(GENERIC_MODAL)),
          },
          confirmProps: {
            text: 'Delete',
            onClick: () => {
              onDeleteCgm(cgmNumber);
            },
            danger: true,
          },
          loadingSelector: (state: RootState) =>
            createLoadingSelector([DELETE_CGM])(state),
        } as GenericModalProps),
      );
    },
    [dispatch, onDeleteCgm],
  );

  const onSave = useCallback(
    (changes: TableEditState<Partial<CGM>>) => {
      const values: Partial<CGM> = get(changes, 'updated', {});
      updateCgmPromiseCreator(
        {
          cgmNumber: values?.cgmNumber,
          type: values?.cgmType,
          description: values?.description,
          omnibusAccountId: accountId,
          isTme: true,
          urlParams: { accountId, cgmId: changes?.original?.cgmNumber },
        },
        dispatch,
      ).then(() => {
        fetchCgms();
      });
    },
    [accountId, dispatch, fetchCgms],
  );
  return (
    <Page header="CGM Management (CDE)">
      <Header>
        <AccountSelectorFCM />
        <AddCGM
          primary
          onClick={onAddNewCgm}
          text="Add new CDE CGM"
          disabled={!canWrite}
        />
      </Header>
      <EMPTable8
        title="CgmTable"
        data={data}
        columns={cgmManagementTMEMetadata(canWrite)}
        fetchData={fetchCgms}
        onSaveChanges={onSave}
        count={data?.length || 0}
        loading={loading}
        loadingText="Fetching CGMs"
        noDataText="No CGMs found"
        minimumRows={5}
        paginated
        local
        showActions
        showActiveFilters
        additionalActions={(row: Row<CGM>) => [
          {
            text: 'Delete',
            onClick: () => onDeleteClicked(row),
            fontSize: '14',
            color: colors.RED_ERROR,
            disabled: !canWrite,
          },
        ]}
      />
    </Page>
  );
};

export default CgmManagement;
